import styled from 'styled-components';
import tw from 'twin.macro';

import useProjects from '../../api/queries/useProjects';
import { Background, Button, FilterInput, Icon, Sidebar, Checkbox } from '@/components';
import { ButtonColor, ButtonSize, IconSize, Icons } from '../../enums';
import strings from '../../language/Swedish';
import { useNavigate } from 'react-router-dom';
import { PATH } from '../../routes/Paths';
import { useState } from 'react';
import useCreateProject from '../../api/mutations/useCreateProject';
import { formatDateToString } from '../../helpers';
import useDeleteProject from '@/api/mutations/useDeleteProject';
import * as Sentry from '@sentry/react';

const PageContainer = styled.div`
  ${tw`flex h-screen`};
`;

const Page = styled.main`
  ${tw`relative flex flex-col flex-1 overflow-y-auto sm:p-4 sm:gap-4`}
`;

const Container = styled.div`
  ${tw`relative z-10 w-full h-full py-0 md:py-6 md:grid md:place-items-center`}
`;

const ListContainer = styled.div`
  ${tw`w-full max-w-[550px] md:m-auto px-2 sm:p-6 sm:rounded-2xl backdrop-blur sm:border sm:border-white/80 sm:bg-white/40 min-h-80 min-h-[calc(100dvh-3rem)]`}
`;

const HeaderContainer = styled.div`
  ${tw`flex items-center justify-between text-xl mb-3 sm:mb-6 py-1 pr-10 sm:pr-0 sticky sm:relative top-0 bg-white border-b border-white sm:border-none sm:bg-white/0 z-10`}
`;

const Header = styled.h1`
  ${tw`flex-1 text-xl text-center md:text-left font-medium`}
`;

const ContentContainer = styled.div`
  ${tw`relative z-0`}
`;

const FilterContainer = styled.div`
  ${tw`flex items-center justify-between gap-2 flex-nowrap w-full`}
    @media (max-width: 400px) {
    flex-wrap: wrap;
  }
`;

const List = styled.ul`
  ${tw`mt-4`}
`;

const ListItem = styled.li`
  ${tw`w-full gap-3 transition-colors items-center justify-start text-left rounded-xl`}
  :focus {
    ${tw`outline-primary`}
  }

  :active {
    ${tw`outline outline-slate-800/20 outline-offset-2`}
  }
`;

const ListLink = styled.a`
  ${tw`flex-1 w-full flex gap-2 p-1.5 items-center bg-transparent`}

  :hover {
    ${tw`bg-transparent`}
  }
`;

const ImageContainer = styled.span`
  ${tw`flex-none w-12 h-12 rounded-lg grid place-content-center border border-slate-300`}

  :hover {
    ${tw``}
  }
`;

const Text = styled.div`
  ${tw`flex-1 flex flex-col leading-tight`}
`;

const Name = styled.span`
  ${tw`flex-1 flex flex-col text-slate-700 leading-tight`}
`;

const CreatedAt = styled.span`
  ${tw`text-xs text-slate-500`}
`;

const Projects = () => {
  const [filterValue, setFilterValue] = useState('');
  const { data: projectsData } = useProjects(1000);
  const [selected,setSelected] = useState<string[]>([]);
  const [edit,setEdit] = useState<boolean>(false);
  const [deleted,setDeleted] = useState<string[]>([]);
  const navigate = useNavigate();
  const [infoText,setInfoText] = useState<string>("");
  const { mutate: createMutation } = useCreateProject({
    onSuccess: (result) => {
      if (result && result.project.id !== undefined) {
        navigate(`${PATH.project}/${result.project.id}`);
      }
    },
  });

  const handleProjectClick = (id: string) => {
    if(edit){
      updateSelected(id);
    } else {
    navigate(`${PATH.project}/${id}`);
    }
  };

  const updateSelected = (id: string) => {
    if(selected.indexOf(id) !== -1){
      const updatedSelected = selected.filter((projectId:string) => projectId !== id);
      setSelected(updatedSelected);
    } else {
      setSelected([...selected,id]);
    }
  }

  const deleteProjects = () => {
    if (confirm(strings.dialog.areYouSure) == true) {
    selected.forEach((id) => {
      deleteMutation({ id: id });
    });
    setEdit(false);
    }
  };

  const { mutate: deleteMutation } = useDeleteProject({
    onSuccess: () => {
      setDeleted(selected);
      setSelected([]);
      setInfoText(strings.data.deleteMultipleSuccess.replace("@length",selected.length.toString()));
    },
    onError: (e) => {
      setInfoText(strings.data.somethingWentWrong);
        Sentry.captureException(e);
    }
  });

  return (
    <PageContainer>
      <Background />
      <Sidebar defaultFolded={true} />
      <Page>
        <Container>
          <ListContainer>
            <HeaderContainer>
              <Header>{strings.sidebar.savedProjects}</Header>
            </HeaderContainer>
            <ContentContainer>
              <FilterContainer>
                <div className="w-full">
                <FilterInput
                  filterValue={filterValue}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilterValue(e.target.value)
                  }
                  clearFilter={() => setFilterValue('')}
                />
                </div>
                <Button
                  color={ButtonColor.primary}
                  size={ButtonSize.large}
                  icon={Icons.plus}
                  className='whitespace-nowrap'
                  onClick={createMutation}>
                  {strings.data.newSearch}
                </Button>
              </FilterContainer>
              <div className="flex gap-4 mt-4">
              <Button color={ButtonColor.primary} size={ButtonSize.medium} icon={Icons.edit} onClick={() => setEdit(!edit)}>{strings.data.editProjectsButton}</Button>
              {selected.length > 0 && <Button color={ButtonColor.warning} size={ButtonSize.medium} icon={Icons.trashcan} onClick={deleteProjects}>{strings.data.deleteAllButton}</Button>}
              </div>
              <div className="mt-4">
                {infoText}
              </div>
              <List>
                {projectsData ? (
                  projectsData.project_summaries_response.map((project) => {
                    if (
                      (filterValue &&
                        project.name
                          .toLowerCase()
                          .includes(filterValue.toLowerCase())) ||
                      filterValue === ''
                    ) {
                      
                      return (
                        <ListItem key={project.id} className={`hover:bg-slate-800/5 hover:rounded-lg ${selected.indexOf(project.id) !== -1 && "bg-slate-800/5"} ${deleted.indexOf(project.id) !== -1 ? "hidden " : "flex"}`}>
                          {edit && <Checkbox className="ml-2" id={project.id} isChecked={selected.indexOf(project.id) !== -1} onToggle={() => updateSelected(project.id)}/>}
                          <ListLink
                            onClick={() => handleProjectClick(project.id)}>
                            <ImageContainer>
                              <Icon icon={Icons.pin3} size={IconSize.medium} />
                            </ImageContainer>
                            <Text>
                              <Name>{project.name}</Name>
                              <CreatedAt>
                                {`${strings.data.created} `}
                                {project.created_at &&
                                  formatDateToString(
                                    new Date(project.created_at)
                                  )}
                              </CreatedAt>
                            </Text>
                          </ListLink>
                        </ListItem>
                      );
                    }
                    return null;
                  })
                ) : (
                  <p>{strings.data.noSavedProjects}</p>
                )}
              </List>
            </ContentContainer>
          </ListContainer>
        </Container>
      </Page>
    </PageContainer>
  );
};

export default Projects;
